// src/features/streams/hooks/useStreamGridEngine.ts

import fuzzysort from 'fuzzysort';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MediaStatusType, MediaStateType } from '@lib2/model/media/MediaTypes';
import { StreamUtils } from '../StreamUtils';
import { useStreamGridState } from '../stores/streamGridState';
import { LIVE_STATUS_RANK } from '@/lib/constants/stream';
import { recordDuration, recordSample } from '@/shared/utils/perf';
import type { IBackendLiveData } from '@/lib/types/Customer';
import type { SharedLinkConfig } from '@/lib/types/link';
import type { SearchMode } from '@/lib/types/Media';
import type { SortDirection } from '@/lib/types/common';
import type {
  StreamViewData,
  StreamGridRow,
  StreamSortKey,
  RealtimeStreamData,
  StreamLiveStatus,
} from '@/lib/types/Stream';

// --- Cache limits ---
const GRID_INDEX_CACHE_LIMIT = 3;
const SEARCH_CACHE_LIMIT = 12;
const ROW_CACHE_LIMIT = 3;
const ROW_VIEW_MODEL_CACHE_LIMIT = 6000;

// --- Status sort ranking ---
const STATUS_SORT_RANK: Record<string, number> = {
  [MediaStatusType.AwaitingIngest]: 0,
  [MediaStatusType.Ingesting]: 1,
  [MediaStatusType.Processing]: 2,
  [MediaStatusType.Ready]: 3,
  [MediaStatusType.Error]: 4,
  [MediaStatusType.Deleted]: 5,
};

// --- Types ---

interface SortState {
  key: StreamSortKey;
  direction: SortDirection;
}

interface SearchState {
  mode: SearchMode;
  text: string;
}

export interface UseStreamGridEngineArgs {
  customerId: number | null;
  streams: StreamViewData[];
  sort: SortState;
  search: SearchState;
  liveStatusMap: Map<number, RealtimeStreamData>;
  liveBackends: IBackendLiveData[];
  linkConfig: SharedLinkConfig | null;
}

interface TimingStats {
  searchMs: number;
  sortMs: number;
  sortBuildMs: number;
  totalMs: number;
}

interface DatasetRecord {
  id: number;
  source: StreamViewData;
  title: string;
  refId: string | null;
  descShort: string | null;
  descLong: string | null;
  tags: string[];
  status: string;
  state: string;
  published: number;
  updated: number;
  backendId: number;
  streamType: string;
  lowerTitle: string | null;
  lowerRefId: string | null;
  lowerDescShort: string | null;
  lowerDescLong: string | null;
  lowerTags: string[] | null;
  statusRank: number | null;
}

interface SortOrder {
  asc: number[];
  desc: number[];
}

interface FuzzyPreparedRecord {
  id: number;
  preparedTitle: ReturnType<typeof fuzzysort.prepare>;
}

interface FuzzyPreparedRefIdRecord {
  id: number;
  preparedRefId: ReturnType<typeof fuzzysort.prepare>;
}

/** One prepared target per record for an optional text field (tags joined, descriptions). */
interface FuzzyPreparedFieldRecord {
  id: number;
  prepared: ReturnType<typeof fuzzysort.prepare>;
}

interface StreamGridIndex {
  cacheKey: string;
  signature: string;
  idToStream: Map<number, StreamViewData>;
  sourceOrderIds: number[];
  sourceOrderIsUpdatedDesc: boolean;
  records: DatasetRecord[];
  sortOrders: Partial<Record<StreamSortKey, SortOrder>>;
  exactSearchCache: Map<string, Set<number>>;
  fuzzySearchCache: Map<string, Set<number>>;
  fuzzyPreparedRecords: FuzzyPreparedRecord[] | null;
  fuzzyPreparedRefIds: FuzzyPreparedRefIdRecord[] | null;
  fuzzyPreparedTags: FuzzyPreparedFieldRecord[] | null;
  fuzzyPreparedDescShorts: FuzzyPreparedFieldRecord[] | null;
  fuzzyPreparedDescLongs: FuzzyPreparedFieldRecord[] | null;
  lastUsedAt: number;
}

interface StreamGridIndexResult {
  index: StreamGridIndex;
  cacheHit: boolean;
}

interface ComputeResult {
  ids: number[];
  timings: TimingStats;
}

interface RowCacheEntry {
  source: StreamViewData;
  contextKey: string;
  row: StreamGridRow;
}

export interface StreamGridEngineStats {
  searchMs: number;
  sortMs: number;
  totalMs: number;
}

export interface UseStreamGridEngineResult {
  count: number;
  orderedIds: number[];
  getIdByIndex: (index: number) => number | undefined;
  getRowByIndex: (index: number) => StreamGridRow | null;
  resultCount: number;
  isComputing: boolean;
  stats: StreamGridEngineStats;
}

// --- Module-level caches ---
const indexCache = new Map<string, StreamGridIndex>();
const persistedRowCaches = new Map<string, Map<number, RowCacheEntry>>();

// --- Utilities ---

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getStatusRank(status: string): number {
  const rank = STATUS_SORT_RANK[status];
  return rank == null ? Number.MAX_SAFE_INTEGER : rank;
}

function getDatasetSignature(streams: StreamViewData[]): string {
  const count = streams.length;
  if (count === 0) return '0:0:0:0';

  const firstId = streams[0]?.media.mediaId ?? 0;
  const lastId = streams[count - 1]?.media.mediaId ?? 0;
  let maxUpdated = 0;

  for (const stream of streams) {
    if (stream.media.updated > maxUpdated) {
      maxUpdated = stream.media.updated;
    }
  }

  return `${count}:${maxUpdated}:${firstId}:${lastId}`;
}

function getOrderQueryKey(sort: SortState, search: SearchState): string {
  return `${search.mode}:${search.text.trim().toLowerCase()}:${sort.key}:${sort.direction}`;
}

function tagsEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

function isSourceOrderUpdatedDesc(streams: StreamViewData[]): boolean {
  for (let i = 1; i < streams.length; i += 1) {
    const prev = streams[i - 1]?.media;
    const next = streams[i]?.media;
    if (!prev || !next) return false;
    if (prev.updated < next.updated) return false;
    if (prev.updated === next.updated && prev.mediaId < next.mediaId) return false;
  }
  return true;
}

// --- Index cache management ---

function touchIndex(cacheKey: string, index: StreamGridIndex): void {
  index.lastUsedAt = now();
  indexCache.delete(cacheKey);
  indexCache.set(cacheKey, index);

  while (indexCache.size > GRID_INDEX_CACHE_LIMIT) {
    const oldestKey = indexCache.keys().next().value;
    if (oldestKey == null) return;
    indexCache.delete(oldestKey);
  }
}

function touchSearchCache(cache: Map<string, Set<number>>, key: string, value: Set<number>): void {
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > SEARCH_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey == null) return;
    cache.delete(oldestKey);
  }
}

// --- Index building ---

function buildIndex(customerId: number | null, signature: string, streams: StreamViewData[]): StreamGridIndex {
  const idToStream = new Map<number, StreamViewData>();
  const sourceOrderIds: number[] = [];
  const records: DatasetRecord[] = [];

  for (const stream of streams) {
    const id = stream.media.mediaId;
    idToStream.set(id, stream);
    sourceOrderIds.push(id);
    records.push({
      id,
      source: stream,
      title: stream.media.title,
      // The list API omits empty fields, so these can be undefined at runtime
      // even though the wire types declare string | null / string[].
      refId: stream.media.refId ?? null,
      descShort: stream.media.descShort ?? null,
      descLong: stream.media.descLong ?? null,
      tags: stream.tags ?? [],
      status: String(stream.media.mediaStatusType),
      state: String(stream.media.mediaStateType),
      published: stream.media.published,
      updated: stream.media.updated,
      backendId: stream.stream?.core?.backendId ?? 0,
      streamType: stream.stream?.core?.streamType ?? 'push',
      lowerTitle: null,
      lowerRefId: null,
      lowerDescShort: null,
      lowerDescLong: null,
      lowerTags: null,
      statusRank: null,
    });
  }

  return {
    cacheKey: `stream:${customerId ?? 0}:${signature}`,
    signature,
    idToStream,
    sourceOrderIds,
    sourceOrderIsUpdatedDesc: isSourceOrderUpdatedDesc(streams),
    records,
    sortOrders: {},
    exactSearchCache: new Map(),
    fuzzySearchCache: new Map(),
    fuzzyPreparedRecords: null,
    fuzzyPreparedRefIds: null,
    fuzzyPreparedTags: null,
    fuzzyPreparedDescShorts: null,
    fuzzyPreparedDescLongs: null,
    lastUsedAt: now(),
  };
}

function syncCachedIndex(index: StreamGridIndex, streams: StreamViewData[]): boolean {
  if (index.records.length !== streams.length) return false;

  const nextById = new Map<number, StreamViewData>();
  const nextSourceOrderIds: number[] = [];
  for (const stream of streams) {
    nextById.set(stream.media.mediaId, stream);
    nextSourceOrderIds.push(stream.media.mediaId);
  }

  let needsSortReset = false;
  let needsSearchReset = false;

  for (const record of index.records) {
    const nextStream = nextById.get(record.id);
    if (!nextStream) return false;

    if (record.source === nextStream) continue;

    const nextTitle = nextStream.media.title;
    const nextRefId = nextStream.media.refId ?? null;
    const nextDescShort = nextStream.media.descShort ?? null;
    const nextDescLong = nextStream.media.descLong ?? null;
    const nextTags = nextStream.tags ?? [];
    const nextStatus = String(nextStream.media.mediaStatusType);
    const nextState = String(nextStream.media.mediaStateType);
    const nextPublished = nextStream.media.published;
    const nextUpdated = nextStream.media.updated;
    const nextBackendId = nextStream.stream?.core?.backendId ?? 0;

    if (
      record.title !== nextTitle ||
      record.status !== nextStatus ||
      record.state !== nextState ||
      record.published !== nextPublished ||
      record.updated !== nextUpdated ||
      record.backendId !== nextBackendId
    ) {
      needsSortReset = true;
    }

    if (
      record.title !== nextTitle ||
      record.refId !== nextRefId ||
      record.descShort !== nextDescShort ||
      record.descLong !== nextDescLong ||
      !tagsEqual(record.tags, nextTags)
    ) {
      needsSearchReset = true;
    }

    record.source = nextStream;
    record.title = nextTitle;
    record.refId = nextRefId;
    record.descShort = nextDescShort;
    record.descLong = nextDescLong;
    record.tags = nextTags;
    record.status = nextStatus;
    record.state = nextState;
    record.published = nextPublished;
    record.updated = nextUpdated;
    record.backendId = nextBackendId;
    record.streamType = nextStream.stream?.core?.streamType ?? 'push';
    record.lowerTitle = null;
    record.lowerRefId = null;
    record.lowerDescShort = null;
    record.lowerDescLong = null;
    record.lowerTags = null;
    record.statusRank = null;
  }

  index.sourceOrderIds = nextSourceOrderIds;
  index.sourceOrderIsUpdatedDesc = isSourceOrderUpdatedDesc(streams);
  index.idToStream = nextById;

  if (needsSortReset) {
    index.sortOrders = {};
  }

  if (needsSearchReset) {
    index.exactSearchCache.clear();
    index.fuzzySearchCache.clear();
    index.fuzzyPreparedRecords = null;
    index.fuzzyPreparedRefIds = null;
    index.fuzzyPreparedTags = null;
    index.fuzzyPreparedDescShorts = null;
    index.fuzzyPreparedDescLongs = null;
  }

  return true;
}

function getOrCreateIndex(customerId: number | null, streams: StreamViewData[]): StreamGridIndexResult {
  const signature = getDatasetSignature(streams);
  const cacheKey = `stream:${customerId ?? 0}:${signature}`;
  const cached = indexCache.get(cacheKey);

  if (cached) {
    const cacheIsValid = syncCachedIndex(cached, streams);
    if (cacheIsValid) {
      touchIndex(cacheKey, cached);
      return { index: cached, cacheHit: true };
    }
    indexCache.delete(cacheKey);
  }

  const index = buildIndex(customerId, signature, streams);
  touchIndex(cacheKey, index);
  return { index, cacheHit: false };
}

// --- Sort helpers ---

function getLowerTitle(record: DatasetRecord): string {
  if (record.lowerTitle !== null) return record.lowerTitle;
  record.lowerTitle = record.title.toLowerCase();
  return record.lowerTitle;
}

function getLowerRefId(record: DatasetRecord): string | null {
  if (record.refId == null || record.refId.length === 0) return null;
  if (record.lowerRefId !== null) return record.lowerRefId;
  record.lowerRefId = record.refId.toLowerCase();
  return record.lowerRefId;
}

function getLowerDescShort(record: DatasetRecord): string | null {
  if (record.descShort == null || record.descShort.length === 0) return null;
  if (record.lowerDescShort !== null) return record.lowerDescShort;
  record.lowerDescShort = record.descShort.toLowerCase();
  return record.lowerDescShort;
}

function getLowerDescLong(record: DatasetRecord): string | null {
  if (record.descLong == null || record.descLong.length === 0) return null;
  if (record.lowerDescLong !== null) return record.lowerDescLong;
  record.lowerDescLong = record.descLong.toLowerCase();
  return record.lowerDescLong;
}

function getLowerTags(record: DatasetRecord): string[] {
  if (record.lowerTags !== null) return record.lowerTags;
  record.lowerTags = record.tags.map((tag) => tag.toLowerCase());
  return record.lowerTags;
}

function getRecordStatusRank(record: DatasetRecord): number {
  if (record.statusRank !== null) return record.statusRank;
  record.statusRank = getStatusRank(record.status);
  return record.statusRank;
}

/**
 * Resolve live status for a record from the live status map.
 * Called during live-status sort; result is NOT cached on the record
 * because live status changes every poll cycle.
 */
function getLiveStatusForRecord(
  record: DatasetRecord,
  liveStatusMap: Map<number, RealtimeStreamData>,
): StreamLiveStatus {
  const entry = liveStatusMap.get(record.id);
  if (!entry) return 'offline';
  return entry.isRecording ? 'recording' : 'live';
}

function getLiveStatusRank(status: StreamLiveStatus): number {
  return LIVE_STATUS_RANK[status];
}

function getLocationLabel(
  record: DatasetRecord,
  backendMap: Map<number, IBackendLiveData>,
): string {
  return backendMap.get(record.backendId)?.locationLabel ?? '';
}

/**
 * Compare two records for ascending sort by key.
 * For liveStatus and location, context maps are required.
 */
function compareRecordAsc(
  a: DatasetRecord,
  b: DatasetRecord,
  key: StreamSortKey,
  liveStatusMap: Map<number, RealtimeStreamData>,
  backendMap: Map<number, IBackendLiveData>,
): number {
  switch (key) {
    case 'title': {
      const aTitle = getLowerTitle(a);
      const bTitle = getLowerTitle(b);
      if (aTitle < bTitle) return -1;
      if (aTitle > bTitle) return 1;
      return a.id - b.id;
    }
    case 'liveStatus': {
      const aRank = getLiveStatusRank(getLiveStatusForRecord(a, liveStatusMap));
      const bRank = getLiveStatusRank(getLiveStatusForRecord(b, liveStatusMap));
      if (aRank < bRank) return -1;
      if (aRank > bRank) return 1;
      return a.id - b.id;
    }
    case 'location': {
      const aLoc = getLocationLabel(a, backendMap).toLowerCase();
      const bLoc = getLocationLabel(b, backendMap).toLowerCase();
      if (aLoc < bLoc) return -1;
      if (aLoc > bLoc) return 1;
      return a.id - b.id;
    }
    case 'status': {
      const aStatus = getRecordStatusRank(a);
      const bStatus = getRecordStatusRank(b);
      if (aStatus < bStatus) return -1;
      if (aStatus > bStatus) return 1;
      return a.id - b.id;
    }
    case 'published':
      if (a.published < b.published) return -1;
      if (a.published > b.published) return 1;
      return a.id - b.id;
    case 'updated':
      if (a.updated < b.updated) return -1;
      if (a.updated > b.updated) return 1;
      return a.id - b.id;
    default:
      return a.id - b.id;
  }
}

/**
 * Build and cache sort order for a given key.
 *
 * IMPORTANT: liveStatus and location sort orders depend on external context
 * (liveStatusMap, backendMap) that can change independently of the dataset.
 * These are invalidated by the caller when context changes.
 */
function ensureSortOrder(
  index: StreamGridIndex,
  key: StreamSortKey,
  liveStatusMap: Map<number, RealtimeStreamData>,
  backendMap: Map<number, IBackendLiveData>,
): { order: SortOrder; buildMs: number } {
  const cached = index.sortOrders[key];
  if (cached) {
    return { order: cached, buildMs: 0 };
  }

  const buildStart = now();
  const ascRecords = [...index.records].sort((a, b) =>
    compareRecordAsc(a, b, key, liveStatusMap, backendMap),
  );
  const asc = ascRecords.map((r) => r.id);
  const order: SortOrder = {
    asc,
    desc: [...asc].reverse(),
  };

  index.sortOrders[key] = order;
  return { order, buildMs: now() - buildStart };
}

// --- Search ---

function ensureFuzzyPreparedRecords(index: StreamGridIndex): FuzzyPreparedRecord[] {
  if (index.fuzzyPreparedRecords) return index.fuzzyPreparedRecords;

  index.fuzzyPreparedRecords = index.records.map((record) => ({
    id: record.id,
    preparedTitle: fuzzysort.prepare(record.title),
  }));

  return index.fuzzyPreparedRecords;
}

function ensureFuzzyPreparedRefIds(index: StreamGridIndex): FuzzyPreparedRefIdRecord[] {
  if (index.fuzzyPreparedRefIds) return index.fuzzyPreparedRefIds;

  const prepared: FuzzyPreparedRefIdRecord[] = [];
  for (const record of index.records) {
    if (record.refId != null && record.refId.length > 0) {
      prepared.push({
        id: record.id,
        preparedRefId: fuzzysort.prepare(record.refId),
      });
    }
  }

  index.fuzzyPreparedRefIds = prepared;
  return prepared;
}

function ensureFuzzyPreparedField(
  index: StreamGridIndex,
  key: 'fuzzyPreparedTags' | 'fuzzyPreparedDescShorts' | 'fuzzyPreparedDescLongs',
  getText: (record: DatasetRecord) => string | null
): FuzzyPreparedFieldRecord[] {
  const cached = index[key];
  if (cached) return cached;

  const prepared: FuzzyPreparedFieldRecord[] = [];
  for (const record of index.records) {
    const text = getText(record);
    if (text != null && text.length > 0) {
      prepared.push({
        id: record.id,
        prepared: fuzzysort.prepare(text),
      });
    }
  }

  index[key] = prepared;
  return prepared;
}

function getExactMatchSet(index: StreamGridIndex, queryLower: string): Set<number> {
  const cached = index.exactSearchCache.get(queryLower);
  if (cached) {
    touchSearchCache(index.exactSearchCache, queryLower, cached);
    return cached;
  }

  // Legacy-parity field set (MediaSearch.searchByRegex): title, refId, tags,
  // descShort, descLong — ordered cheapest first so the common title hit
  // short-circuits the rest.
  const matched = new Set<number>();
  for (const record of index.records) {
    if (getLowerTitle(record).includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    const lowerRefId = getLowerRefId(record);
    if (lowerRefId !== null && lowerRefId.includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    if (record.tags.length > 0 && getLowerTags(record).some((tag) => tag.includes(queryLower))) {
      matched.add(record.id);
      continue;
    }
    const lowerDescShort = getLowerDescShort(record);
    if (lowerDescShort !== null && lowerDescShort.includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    const lowerDescLong = getLowerDescLong(record);
    if (lowerDescLong !== null && lowerDescLong.includes(queryLower)) {
      matched.add(record.id);
    }
  }

  touchSearchCache(index.exactSearchCache, queryLower, matched);
  return matched;
}

function getFuzzyMatchSet(index: StreamGridIndex, query: string): Set<number> {
  const queryKey = query.trim().toLowerCase();
  const cached = index.fuzzySearchCache.get(queryKey);
  if (cached) {
    touchSearchCache(index.fuzzySearchCache, queryKey, cached);
    return cached;
  }

  const titleResults = fuzzysort.go(query, ensureFuzzyPreparedRecords(index), {
    key: 'preparedTitle',
    threshold: -10000,
    limit: Infinity,
  });

  const matched = new Set<number>();
  for (const result of titleResults) {
    matched.add(result.obj.id);
  }

  const preparedRefIds = ensureFuzzyPreparedRefIds(index);
  if (preparedRefIds.length > 0) {
    const refIdResults = fuzzysort.go(query, preparedRefIds, {
      key: 'preparedRefId',
      threshold: -10000,
      limit: Infinity,
    });
    for (const result of refIdResults) {
      matched.add(result.obj.id);
    }
  }

  // Legacy-parity fields: tags (joined per record), descShort, descLong.
  const fieldLists: FuzzyPreparedFieldRecord[][] = [
    ensureFuzzyPreparedField(index, 'fuzzyPreparedTags', (record) =>
      record.tags.length > 0 ? record.tags.join(' ') : null
    ),
    ensureFuzzyPreparedField(index, 'fuzzyPreparedDescShorts', (record) => record.descShort),
    ensureFuzzyPreparedField(index, 'fuzzyPreparedDescLongs', (record) => record.descLong),
  ];
  for (const fieldList of fieldLists) {
    if (fieldList.length === 0) continue;
    const fieldResults = fuzzysort.go(query, fieldList, {
      key: 'prepared',
      threshold: -10000,
      limit: Infinity,
    });
    for (const result of fieldResults) {
      matched.add(result.obj.id);
    }
  }

  touchSearchCache(index.fuzzySearchCache, queryKey, matched);
  return matched;
}

function filterOrder(order: number[], allowed: Set<number>): number[] {
  if (allowed.size === order.length) return order;

  const filtered: number[] = [];
  for (const id of order) {
    if (allowed.has(id)) {
      filtered.push(id);
    }
  }

  return filtered;
}

// --- Compute orchestration ---

function computeOrderedIds(
  index: StreamGridIndex,
  search: SearchState,
  sort: SortState,
  liveStatusMap: Map<number, RealtimeStreamData>,
  backendMap: Map<number, IBackendLiveData>,
): ComputeResult {
  const totalStart = now();
  const query = search.text.trim();

  // Search
  const searchStart = now();
  let matchedIds: Set<number> | null = null;
  if (query.length > 0) {
    matchedIds =
      search.mode === 'exact'
        ? getExactMatchSet(index, query.toLowerCase())
        : getFuzzyMatchSet(index, query);
  }
  const searchMs = now() - searchStart;

  // Sort
  const sortStart = now();
  let sortBuildMs = 0;
  let baseOrder: number[];

  if (sort.key === 'updated' && sort.direction === 'desc' && index.sourceOrderIsUpdatedDesc) {
    baseOrder = index.sourceOrderIds;
  } else {
    const ensuredOrder = ensureSortOrder(index, sort.key, liveStatusMap, backendMap);
    sortBuildMs = ensuredOrder.buildMs;
    baseOrder = sort.direction === 'asc' ? ensuredOrder.order.asc : ensuredOrder.order.desc;
  }

  const ids = matchedIds ? filterOrder(baseOrder, matchedIds) : baseOrder;
  const sortMs = now() - sortStart;

  return {
    ids,
    timings: { searchMs, sortMs, sortBuildMs, totalMs: now() - totalStart },
  };
}

// --- Row cache ---

function getPersistentRowCache(cacheKey: string): Map<number, RowCacheEntry> {
  const existing = persistedRowCaches.get(cacheKey);
  if (existing) {
    persistedRowCaches.delete(cacheKey);
    persistedRowCaches.set(cacheKey, existing);
    return existing;
  }

  const created = new Map<number, RowCacheEntry>();
  persistedRowCaches.set(cacheKey, created);

  while (persistedRowCaches.size > ROW_CACHE_LIMIT) {
    const oldestKey = persistedRowCaches.keys().next().value;
    if (oldestKey == null) return created;
    persistedRowCaches.delete(oldestKey);
  }

  return created;
}

// --- Perf reporting ---

function reportOrderTimings(timings: TimingStats, meta: Record<string, unknown>): void {
  recordDuration('streams.search.compute', timings.searchMs, meta);
  recordDuration('streams.sort.compute', timings.sortMs, meta);
  recordDuration('streams.engine.orderCompute', timings.totalMs, meta);
}

// --- Hook ---

/**
 * Stream grid engine -- ID-first pattern following the canonical video grid engine.
 *
 * Key differences from video engine:
 * 1. Live status integration: liveStatusMap used for sort + row models
 * 2. Location resolution: backendMap used for sort + row models
 * 3. Live status version: sort order caches for liveStatus/location are
 *    invalidated when live status data changes
 */
export function useStreamGridEngine({
  customerId,
  streams,
  sort,
  search,
  liveStatusMap,
  liveBackends,
  linkConfig,
}: UseStreamGridEngineArgs): UseStreamGridEngineResult {
  const setOrderedIdsSnapshot = useStreamGridState((s) => s.setOrderedIdsSnapshot);
  const trimmedSearchText = search.text.trim();

  const orderQueryKey = useMemo(
    () => getOrderQueryKey(sort, search),
    [sort.key, sort.direction, search.mode, search.text],
  );

  // Build backend lookup map
  const backendMap = useMemo(() => {
    const map = new Map<number, IBackendLiveData>();
    for (const b of liveBackends) {
      map.set(b.backendId, b);
    }
    return map;
  }, [liveBackends]);

  // Track live status version for cache invalidation.
  // Incremented each time the liveStatusMap reference changes.
  const liveStatusVersionRef = useRef(0);
  const prevLiveStatusMapRef = useRef(liveStatusMap);
  if (liveStatusMap !== prevLiveStatusMapRef.current) {
    liveStatusVersionRef.current += 1;
    prevLiveStatusMapRef.current = liveStatusMap;
  }
  const liveStatusVersion = liveStatusVersionRef.current;

  // Dataset indexing
  const indexedDataset = useMemo(() => {
    const start = now();
    const result = getOrCreateIndex(customerId, streams);

    recordDuration('streams.engine.datasetBuild', now() - start, {
      count: streams.length,
      cacheHit: result.cacheHit,
      signature: result.index.signature,
    });

    return result;
  }, [customerId, streams]);

  const index = indexedDataset.index;

  // Invalidate liveStatus and location sort caches when live data changes.
  // These sorts depend on external context that changes independently of the dataset.
  useEffect(() => {
    delete index.sortOrders.liveStatus;
    // Location sort doesn't change with live status, but could change if backends reload.
    // We leave it cached unless the dataset changes.
  }, [liveStatusVersion, index]);

  // Row cache management
  const rowCache = useMemo(() => {
    const cache = getPersistentRowCache(index.cacheKey);
    for (const id of cache.keys()) {
      if (!index.idToStream.has(id)) {
        cache.delete(id);
      }
    }
    return cache;
  }, [index.cacheKey, index.idToStream]);

  // Perf: path recording
  useEffect(() => {
    recordSample('streams.engine.path', 0, {
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      count: streams.length,
      key: sort.key,
      direction: sort.direction,
    });
  }, [search.mode, trimmedSearchText.length, streams.length, sort.key, sort.direction]);

  // Compute ordered IDs
  const mainResult = useMemo(() => {
    const computed = computeOrderedIds(index, search, sort, liveStatusMap, backendMap);

    reportOrderTimings(computed.timings, {
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      resultCount: computed.ids.length,
      key: sort.key,
      direction: sort.direction,
    });

    return computed;
    // liveStatusVersion included to recompute when live status changes and
    // the current sort key is liveStatus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, search.mode, search.text, sort.key, sort.direction, trimmedSearchText.length, liveStatusMap, backendMap]);

  const orderedIds = mainResult.ids;

  // Persist ordered IDs snapshot to Zustand store (for back-nav restore)
  useEffect(() => {
    setOrderedIdsSnapshot(index.signature, orderQueryKey, orderedIds);
  }, [orderedIds, orderQueryKey, index.signature, setOrderedIdsSnapshot]);

  const stats: StreamGridEngineStats = useMemo(
    () => ({
      searchMs: mainResult.timings.searchMs,
      sortMs: mainResult.timings.sortMs,
      totalMs: mainResult.timings.totalMs,
    }),
    [mainResult.timings.searchMs, mainResult.timings.sortMs, mainResult.timings.totalMs],
  );

  // Context key for row cache invalidation: includes live status version
  // so that rows re-render when polling updates live/recording status, and
  // link config presence so thumbnails appear once the config loads.
  const contextKey = useMemo(
    () => `lsv:${liveStatusVersion}:backends:${liveBackends.length}:${linkConfig ? 'with-link' : 'no-link'}`,
    [liveStatusVersion, liveBackends.length, linkConfig],
  );

  const getIdByIndex = useCallback(
    (indexValue: number) => orderedIds[indexValue],
    [orderedIds],
  );

  const getRowByIndex = useCallback(
    (indexValue: number): StreamGridRow | null => {
      const id = orderedIds[indexValue];
      if (id == null) return null;

      const stream = index.idToStream.get(id);
      if (!stream) return null;

      const cached = rowCache.get(id);
      if (cached && cached.source === stream && cached.contextKey === contextKey) {
        return cached.row;
      }

      const row = StreamUtils.buildRow(stream, liveStatusMap, backendMap, linkConfig);

      rowCache.set(id, { source: stream, contextKey, row });

      while (rowCache.size > ROW_VIEW_MODEL_CACHE_LIMIT) {
        const oldestId = rowCache.keys().next().value;
        if (oldestId == null) break;
        rowCache.delete(oldestId);
      }

      return row;
    },
    [orderedIds, index.idToStream, rowCache, contextKey, liveStatusMap, backendMap, linkConfig],
  );

  return {
    count: orderedIds.length,
    orderedIds,
    getIdByIndex,
    getRowByIndex,
    resultCount: orderedIds.length,
    isComputing: false,
    stats,
  };
}
